/**
 * RealtimeEtaCard — small badge/card showing live ETA from /realtime-eta.
 * Polls every 8 seconds while shipment is `in_transit`.
 *
 * Usage:
 *   <RealtimeEtaCard trackingCode="BM-2026-00147" status={shipment.status} />
 *
 * Variants:
 *   - inline (default): pill chip — for cards
 *   - card: full bento-item with progress bar — for expanded views
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Clock, Path, Lightning } from "@phosphor-icons/react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function RealtimeEtaCard({ trackingCode, status, variant = "card" }) {
  const { t } = useTranslation();
  const [eta, setEta] = useState(null);

  useEffect(() => {
    if (status !== "in_transit") return;
    let cancelled = false;
    const fetchOnce = async () => {
      try {
        const res = await axios.get(`${API}/shipments/${trackingCode}/realtime-eta`);
        if (!cancelled && res.data?.in_simulation) setEta(res.data);
      } catch (e) { /* ignore */ }
    };
    fetchOnce();
    const id = setInterval(fetchOnce, 8000);
    return () => { cancelled = true; clearInterval(id); };
  }, [trackingCode, status]);

  if (status !== "in_transit" || !eta) return null;

  const minsLabel = eta.remaining_minutes < 1
    ? t("eta.less_than_minute")
    : eta.remaining_minutes < 60
      ? `${Math.round(eta.remaining_minutes)} ${t("eta.minutes")}`
      : new Date(eta.eta_iso).toLocaleTimeString("pt-PT", { hour: "2-digit", minute: "2-digit" });

  if (variant === "inline") {
    return (
      <span
        data-testid={`realtime-eta-inline-${trackingCode}`}
        className="flex items-center gap-1"
        style={{ fontSize: "0.7rem", padding: "3px 10px", borderRadius: 9999, background: "rgba(37,99,235,0.08)", color: "#2563eb", fontWeight: 600 }}
        title={`${eta.progress_pct}% completo · ${eta.speed_kmh} km/h`}
      >
        <Clock size={11} weight="duotone" />
        {minsLabel}
      </span>
    );
  }

  return (
    <div
      data-testid={`realtime-eta-card-${trackingCode}`}
      className="bento-item p-5"
      style={{ marginTop: 12, background: "linear-gradient(135deg, rgba(37,99,235,0.06), rgba(45,74,62,0.04))", border: "1px solid rgba(37,99,235,0.2)" }}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Lightning size={18} weight="duotone" style={{ color: "#2563eb" }} />
          <span style={{ fontFamily: "Outfit", fontWeight: 700, fontSize: "0.9rem" }}>{t("eta.title")}</span>
        </div>
        <span style={{ fontSize: "0.7rem", color: "var(--text-muted)" }}>{eta.speed_kmh} km/h</span>
      </div>
      <div className="flex items-baseline gap-2 mb-3">
        <span style={{ fontFamily: "Outfit", fontWeight: 700, fontSize: "1.6rem", color: "#2563eb" }}>{minsLabel}</span>
        <span style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}>
          ({t("eta.arrives_at")} {new Date(eta.eta_iso).toLocaleTimeString("pt-PT", { hour: "2-digit", minute: "2-digit" })})
        </span>
      </div>
      {/* Progress bar */}
      <div style={{ width: "100%", height: 8, borderRadius: 9999, background: "rgba(37,99,235,0.1)", overflow: "hidden", marginBottom: 8 }}>
        <div
          data-testid={`realtime-eta-progress-${trackingCode}`}
          style={{ width: `${eta.progress_pct}%`, height: "100%", background: "linear-gradient(90deg, var(--primary), #2563eb)", transition: "width 1s ease" }}
        />
      </div>
      <div className="flex items-center justify-between" style={{ fontSize: "0.7rem", color: "var(--text-muted)" }}>
        <span className="flex items-center gap-1"><Path size={11} /> {eta.origin}</span>
        <span style={{ fontWeight: 600 }}>{eta.progress_pct}%</span>
        <span className="flex items-center gap-1">{eta.destination} <Path size={11} /></span>
      </div>
    </div>
  );
}
